import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CreateMealRecordDto, MealRecord, MealReportResponse, UpdateMealRecordDto } from '../models/meal-record.model';

@Injectable({ providedIn: 'root' })
export class MealRecordsService {
  private http = inject(HttpClient);
  private base = `${environment.apiUrl}/MealRecords`;

  getAll(filter?: { clientId?: string; from?: string; to?: string; page?: number; pageSize?: number }): Observable<MealRecord[]> {
    let params = new HttpParams();
    if (filter?.clientId) params = params.set('clientId', filter.clientId);
    if (filter?.from) params = params.set('from', filter.from);
    if (filter?.to) params = params.set('to', filter.to);
    if (filter?.page) params = params.set('page', filter.page);
    if (filter?.pageSize) params = params.set('pageSize', filter.pageSize);
    return this.http.get<MealRecord[]>(this.base, { params }).pipe(catchError(this.handle));
  }

  getByClient(clientId: string): Observable<MealRecord[]> {
    return this.http.get<MealRecord[]>(`${this.base}/by-client/${clientId}`).pipe(catchError(this.handle));
  }

  getById(id: string): Observable<MealRecord> {
    return this.http.get<MealRecord>(`${this.base}/${id}`).pipe(catchError(this.handle));
  }

  create(dto: CreateMealRecordDto): Observable<MealRecord> {
    return this.http.post<MealRecord>(this.base, dto).pipe(catchError(this.handle));
  }

  update(id: string, dto: UpdateMealRecordDto): Observable<MealRecord> {
    return this.http.put<MealRecord>(`${this.base}/${id}`, dto).pipe(catchError(this.handle));
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}`).pipe(catchError(this.handle));
  }

  todayCount(): Observable<number> {
    return this.http.get<{ count: number }>(`${this.base}/today-count`).pipe(
      catchError(this.handle),
      (source$) => new Observable<number>((subscriber) => {
        const subscription = source$.subscribe({
          next: (res) => subscriber.next(res.count),
          error: (e) => subscriber.error(e),
          complete: () => subscriber.complete(),
        });
        return () => subscription.unsubscribe();
      })
    );
  }

  totalMeals(): Observable<number> {
    return this.http.get<{ count: number }>(`${this.base}/total-meals`).pipe(
      catchError(this.handle),
      (source$) => new Observable<number>((subscriber) => {
        const subscription = source$.subscribe({
          next: (res) => subscriber.next(res.count),
          error: (e) => subscriber.error(e),
          complete: () => subscriber.complete(),
        });
        return () => subscription.unsubscribe();
      })
    );
  }

  recent(): Observable<MealRecord[]> {
    return this.http.get<MealRecord[]>(`${this.base}/recent`).pipe(catchError(this.handle));
  }

  report(clientId: string, from: string, to: string): Observable<MealReportResponse> {
    const body = { clientId, dateFrom: from, dateTo: to } as any;
    return this.http.post<MealReportResponse>(`${this.base}/report`, body).pipe(catchError(this.handle));
  }

  private handle(err: any) {
    return throwError(() => err);
  }
}
